<template>
  <div class="info">
    <header class="info__hero">
      <div>
        <span class="info__hero-icon" aria-hidden="true">
          <svg viewBox="0 0 32 32" role="presentation">
            <circle
              cx="16"
              cy="16"
              r="12"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
            />
            <path
              d="M16 8v7l4 3"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <p class="info__eyebrow">Mealorder Leitfaden</p>
        <h1>So funktioniert deine Bestellung</h1>
        <p>
          Wann kannst du bestellen, bis wann kannst du etwas &auml;ndern und
          welche Mails kommen wann? Hier steht alles Wichtige f&uuml;r dich
          kompakt zusammengefasst.
        </p>
      </div>
      <router-link class="info__back" to="/">
        Zur&uuml;ck zum Speiseplan
      </router-link>
    </header>

    <section class="info__section">
      <h2>
        <span class="info__section-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" role="presentation">
            <rect
              x="3"
              y="5"
              width="18"
              height="16"
              rx="3"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
            />
            <path
              d="M8 3v4M16 3v4M3 11h18"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />
          </svg>
        </span>
        Bestellfenster
      </h2>
      <p>
        Du kannst nur f&uuml;r freigeschaltete Wochentage bestellen (aktuell:
        <strong>{{ allowedWeekdayLabel }}</strong
        >). Sp&auml;testens am
        <strong>Vortag bis {{ deadlineTime }}</strong> m&uuml;ssen neue oder
        ge&auml;nderte Bestellungen eingegangen sein.
      </p>
      <ul class="info__list">
        <li>
          <strong>Freigabe:</strong> Sobald der Speiseplan importiert ist, sind
          die oben genannten Tage f&uuml;r Bestellungen ge&ouml;ffnet.
        </li>
        <li>
          <strong>Letzte Bestellung:</strong> Bis zum Vortag
          {{ deadlineTime }} kannst du bestellen, stornieren oder etwas
          &auml;ndern. Danach wird alles fest an die K&uuml;che &uuml;bergeben.
        </li>
        <li>
          <strong>Wochengerichte:</strong> Sobald ein w&ouml;chentliches Gericht
          hinterlegt ist, erscheint es automatisch. Gibt es keins, siehst du auf
          der Startseite direkt den Hinweis dazu.
        </li>
      </ul>
    </section>

    <section class="info__section">
      <h2>
        <span
          class="info__section-icon info__section-icon--mail"
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" role="presentation">
            <rect
              x="3"
              y="5"
              width="18"
              height="14"
              rx="3"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
            />
            <path
              d="M4 7l8 6 8-6"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        E-Mail Zeitplan
      </h2>
      <div class="info__cards">
        <article
          v-for="mail in emailSchedule"
          :key="mail.title"
          class="info-card"
        >
          <span class="info-card__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" role="presentation">
              <path
                d="M12 6v6l3 2"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <circle
                cx="12"
                cy="12"
                r="9"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
              />
            </svg>
          </span>
          <p class="info-card__when">{{ mail.when }}</p>
          <h3>{{ mail.title }}</h3>
          <p>{{ mail.description }}</p>
        </article>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { EmailScheduleEntry } from "@/types/info";

const WEEKDAY_LABELS = [
  "Sonntag",
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
];

function parseAllowedWeekdays(raw: string | undefined): number[] {
  if (!raw) {
    return [];
  }

  const unique = new Set<number>();
  raw
    .split(",")
    .map((value) => Number.parseInt(value.trim(), 10))
    .forEach((value) => {
      if (!Number.isNaN(value) && value >= 0 && value <= 6) {
        unique.add(value);
      }
    });

  return Array.from(unique).sort((a, b) => a - b);
}

function formatWeekdayList(values: number[]): string {
  if (!values.length) {
    return "";
  }

  const labels = values.map((value) => WEEKDAY_LABELS[value] || `${value}`);
  if (labels.length === 1) {
    return labels[0];
  }

  const last = labels.pop();
  if (!last) {
    return "";
  }

  return `${labels.join(", ")} und ${last}`;
}

export default defineComponent({
  name: "InfoPage",
  data() {
    return {
      deadlineTime: "14:00 Uhr",
      emailSchedule: [
        {
          title: "Sammelbestellung f\u00fcr K\u00fcche/Verwaltung",
          when: "Vortag um 14:00 Uhr",
          description:
            "Direkt nach Ende der Bestellfrist wird die &Uuml;bersicht f\u00fcr den n\u00e4chsten Tag verschickt. Ab dann sind keine &Auml;nderungen mehr m\u00f6glich.",
        },
        {
          title: "Monatliche Abrechnung f\u00fcr HR",
          when: "Am 20. Kalendertag",
          description:
            "Alle best\u00e4tigten Bestellungen des Monats gehen gesammelt als CSV an HR. Eigene Quittungen musst du nicht schicken.",
        },
      ] as EmailScheduleEntry[],
    };
  },
  computed: {
    allowedWeekdays(): number[] {
      return parseAllowedWeekdays(process.env.VUE_APP_POSS_NEXT_ORDER_DATE);
    },
    allowedWeekdayLabel(): string {
      if (!this.allowedWeekdays.length) {
        return "alle freigeschalteten Wochentage";
      }
      return formatWeekdayList(this.allowedWeekdays);
    },
  },
});
</script>

<style scoped>
.info {
  width: min(1160px, 100%);
  margin: 0 auto;
  padding: clamp(2.25rem, 3vw + 1rem, 3.5rem) clamp(1rem, 3vw + 0.75rem, 3rem)
    clamp(2.5rem, 4vw + 1.25rem, 4.25rem);
  color: #fff;
}

.info__hero {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: clamp(1.25rem, 2vw + 0.5rem, 2.75rem);
  margin-bottom: clamp(1.75rem, 3vw + 1.25rem, 3rem);
}

.info__hero-icon {
  width: clamp(52px, 4vw + 36px, 66px);
  height: clamp(52px, 4vw + 36px, 66px);
  border-radius: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ambifox-color);
  margin-bottom: 1rem;
}

.info__hero-icon svg {
  width: clamp(22px, 2vw + 14px, 30px);
  height: clamp(22px, 2vw + 14px, 30px);
}

.info__eyebrow {
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
}

.info__hero h1 {
  margin: 0.4rem 0 0.6rem;
  font-size: clamp(1.6rem, 3.5vw + 0.25rem, 2.6rem);
}

.info__back {
  align-self: flex-start;
  padding: 0.65rem clamp(1.1rem, 2vw + 0.5rem, 1.6rem);
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  text-decoration: none;
  color: #fff;
  transition: background 0.2s ease, transform 0.2s ease;
}

.info__back:hover {
  background: rgba(255, 255, 255, 0.08);
  transform: translateY(-1px);
}

.info__back:focus-visible {
  outline: 2px solid var(--ambifox-color);
  outline-offset: 2px;
}

.info__section {
  margin-top: clamp(1.75rem, 2vw + 1rem, 2.75rem);
  padding: clamp(1.35rem, 2vw + 1rem, 2rem);
  border-radius: 1.75rem;
  background: rgba(6, 10, 24, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.info__section h2 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: clamp(0.6rem, 1vw + 0.4rem, 1rem);
  font-size: clamp(1.1rem, 1.3vw + 1rem, 1.35rem);
}

.info__section-icon {
  width: clamp(38px, 3vw + 26px, 50px);
  height: clamp(38px, 3vw + 26px, 50px);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ambifox-color);
}

.info__section-icon--mail {
  color: #6fb7ff;
}

.info__section p {
  margin: 1rem 0 0;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  font-size: clamp(0.98rem, 0.8vw + 0.9rem, 1.1rem);
}

.info__list {
  list-style: none;
  margin: 1.5rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(0.75rem, 0.8vw + 0.65rem, 1.1rem);
}

.info__list li {
  padding: clamp(0.9rem, 1vw + 0.7rem, 1.2rem)
    clamp(1rem, 1vw + 0.9rem, 1.35rem);
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.info__cards {
  margin-top: 1.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: clamp(0.9rem, 1vw + 0.8rem, 1.35rem);
}

.info-card {
  padding: clamp(1.1rem, 1.3vw + 1rem, 1.6rem);
  border-radius: 1.35rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(
    150deg,
    rgba(12, 16, 30, 0.95),
    rgba(8, 10, 24, 0.9)
  );
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  transition: transform 0.25s ease, box-shadow 0.25s ease,
    border-color 0.25s ease;
}

.info-card:hover {
  transform: translateY(-4px);
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 28px 52px rgba(0, 0, 0, 0.55);
}

.info-card__icon {
  width: clamp(42px, 2.5vw + 32px, 52px);
  height: clamp(42px, 2.5vw + 32px, 52px);
  border-radius: 1.05rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ambifox-color);
  margin-bottom: 0.75rem;
}

.info-card__icon svg {
  width: 24px;
  height: 24px;
}

.info-card__when {
  margin: 0;
  font-size: 0.85rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  white-space: nowrap;
}

.info-card h3 {
  margin: 0.4rem 0 0.5rem;
  font-size: clamp(1.05rem, 1vw + 0.95rem, 1.25rem);
}

.info-card p {
  margin: 0;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.5;
}

.info-card__when + h3 {
  margin-top: 0.35rem;
}

@media (max-width: 1024px) {
  .info__hero {
    grid-template-columns: 1fr;
    align-items: flex-start;
  }

  .info__back {
    justify-self: flex-start;
  }
}

@media (max-width: 720px) {
  .info__hero {
    text-align: center;
  }

  .info__back {
    width: 100%;
    text-align: center;
  }

  .info__section h2 {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 520px) {
  .info {
    padding-inline: clamp(0.75rem, 5vw + 0.25rem, 1.35rem);
  }

  .info__hero {
    gap: 1.35rem;
  }

  .info__section {
    padding: clamp(1rem, 3vw + 0.5rem, 1.35rem);
    border-radius: 1.25rem;
  }

  .info__cards {
    grid-template-columns: 1fr;
  }
}
</style>
